/**
 * <hotel-date-bar> / <hotel-book-bar> / <hotel-package-list> Web Component
 * (B2 壳 + B5/B8/B9 React 接管)
 *
 * 三元素共用 createDateBarElement(App) 工厂,各自接 check-in/check-out 属性(无 mode),
 * <hotel-package-list> 额外接 limit(内联列表房型数量上限,正整数生效,不传=默认 3 个,date-bar/book-bar 忽略):
 *   connectedCallback -> attachShadow -> mountShadowApp(React 渲染对应 App,包错误边界)
 *   attributeChangedCallback(check-in/check-out/limit)-> 更新 props 重渲染
 *   disconnectedCallback -> 卸载 React 树
 *
 * 多实例(B8):每个元素独立 shadow root + React root + 状态;resolveHotel 模块级去重。
 * 安全校验(B4)/加载/错误(B9)均在 React 内处理;此处仅兜底 createRoot 挂载失败。
 *
 * JSX 要求本文件为 .tsx(esbuild .ts loader 不解析 JSX)。
 */
import * as React from 'react';
import type { ShadowAppHandle } from '../lib/react';
import { mountShadowApp } from '../lib/react';
import type { AppProps } from './shared';
import { DateBarApp } from './DateBarApp';
import { BookBarApp } from './BookBarApp';
import { PackageListApp } from './PackageListApp';
import { renderError } from './render';

const ATTR_CHECK_IN = 'check-in';
const ATTR_CHECK_OUT = 'check-out';
const ATTR_LIMIT = 'limit';

/** 解析 limit 属性:正整数生效,其余(缺省/空/非正/NaN)返回 undefined 表示用默认值(3) */
function parseLimit(raw: string | null): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** 工厂:为指定 App 创建一个 Web Component 元素类(共用 check-in/check-out/limit 属性,无 mode) */
function createDateBarElement(App: React.ComponentType<AppProps>) {
  return class HotelDateBarBase extends HTMLElement {
    private shadowApp: ShadowAppHandle | null = null;
    private checkIn: string | null = null;
    private checkOut: string | null = null;
    private limit: number | undefined;

    static get observedAttributes(): string[] {
      return [ATTR_CHECK_IN, ATTR_CHECK_OUT, ATTR_LIMIT];
    }

    connectedCallback(): void {
      // 防重复 attach(move 场景由 React key 处理,此处仅防二次连接)
      if (this.shadowApp) return;
      const shadow = this.attachShadow({ mode: 'open' });
      this.parseAttrs();
      try {
        this.shadowApp = mountShadowApp(shadow, this.renderNode());
      } catch (e) {
        console.error('[HotelSDK] datebar 挂载失败:', e);
        renderError(shadow);
      }
    }

    attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
      if (name === ATTR_CHECK_IN) this.checkIn = value;
      else if (name === ATTR_CHECK_OUT) this.checkOut = value;
      else if (name === ATTR_LIMIT) this.limit = parseLimit(value);
      // 首次属性设置可能在 connectedCallback 之前(shadowApp 还没建),仅记录,连接时 parseAttrs 再渲染
      if (this.shadowApp) this.shadowApp.render(this.renderNode());
    }

    disconnectedCallback(): void {
      this.shadowApp?.unmount();
      this.shadowApp = null;
    }

    private parseAttrs(): void {
      this.checkIn = this.getAttribute(ATTR_CHECK_IN);
      this.checkOut = this.getAttribute(ATTR_CHECK_OUT);
      this.limit = parseLimit(this.getAttribute(ATTR_LIMIT));
    }

    private renderNode(): React.ReactNode {
      return (
        <App
          initialCheckIn={this.checkIn ?? undefined}
          initialCheckOut={this.checkOut ?? undefined}
          limit={this.limit}
          host={this}
        />
      );
    }
  };
}

const HotelDateBarElement = createDateBarElement(DateBarApp);
const HotelBookBarElement = createDateBarElement(BookBarApp);
const HotelPackageListElement = createDateBarElement(PackageListApp);

/**
 * 注册 <hotel-date-bar>(纯日期栏,幂等)。由 index.ts 显式调用,避免依赖副作用 import 被 tree-shake。
 */
export function registerDateBar(): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('hotel-date-bar')) return;
  customElements.define('hotel-date-bar', HotelDateBarElement);
}

/** 注册 <hotel-book-bar>(日期栏 + 预订,幂等) */
export function registerBookBar(): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('hotel-book-bar')) return;
  customElements.define('hotel-book-bar', HotelBookBarElement);
}

/** 注册 <hotel-package-list>(日期栏 + 查询 + 内联列表,幂等) */
export function registerPackageList(): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('hotel-package-list')) return;
  customElements.define('hotel-package-list', HotelPackageListElement);
}

export { HotelDateBarElement, HotelBookBarElement, HotelPackageListElement };
